import * as DetectBrowser from 'detect-browser';
import downloadFile from 'downloadjs';

import { logError } from 'utils/log';
import { get as getRequest } from 'utils/api';

const PREVENT_PARALLEL_DOWNLOADS = ['chrome', 'safari'].includes(DetectBrowser.browserName(navigator.userAgent) ?? '');

// Avoid calling this via Promise.all, it's not possible to download multiple files in parallel in Safari
// Always wait for promise resolution before downloading next file
export async function downloadIndividualFile(attachmentUrl: string): Promise<void> {
  if (PREVENT_PARALLEL_DOWNLOADS) {
    // Chrome has a bug that prevents downloading multiple files. Remove this check once the following
    // bug has been fixed: https://bugs.chromium.org/p/chromium/issues/detail?id=820103
    // Safari seems to have similar issues as well
    await new Promise((next) => { setTimeout(next, 500); });
  }

  return new Promise<void>((resolve) => {
    const link = document.createElement('a');
    link.href = attachmentUrl;
    link.style.display = 'none';
    link.onclick = (event) => {
      event.stopPropagation();
    };
    document.body.appendChild(link);
    setTimeout(() => {
      link.click();
      document.body.removeChild(link);
      resolve();
    }, 50);
  });
}

// Use that one for downloading images and other user uploaded resources from our S3
// for some reasons it's required to proxy through additional XMLHttpRequest, otherwise it only downloads link to file
export function downloadIndividualFileWithProxying(attachmentUrl: string, filename: string): Promise<any> {
  return new Promise<undefined|null>((resolve) => {
    const x = new XMLHttpRequest();
    x.open('GET', attachmentUrl.replace('http://', 'https://'), true);
    x.responseType = 'blob';
    x.onload = e => {
      // @ts-ignore
      downloadFile(e.target.response, filename);
      // Chrome has a bug that prevents downloading multiple files. Remove this check once the following
      // bug has been fixed: https://bugs.chromium.org/p/chromium/issues/detail?id=820103
      if (PREVENT_PARALLEL_DOWNLOADS) {
        setTimeout(resolve, 500);
      } else {
        resolve(undefined);
      }
    };
    x.onerror = (error) => {
      logError(error);
      resolve(null);
    };

    if (PREVENT_PARALLEL_DOWNLOADS) {
      setTimeout(() => {
        x.send();
      }, 500);
    } else {
      x.send();
    }
  });
}

function observeZipStatus(
  jsonFileS3Url: string,
  retriesLeft: number,
  delay: number,
  onSuccess: (url: string | undefined) => void,
  onFail: () => void
): void {
  if (retriesLeft === 0) {
    onFail();
    return;
  }

  getRequest(jsonFileS3Url, {}, { suppressAuth: true, suppressCollabHeaders: true })
    .then((response: any) => {
      switch (response.data.status) {
        case 'failed': {
          onFail();
          break;
        }
        case 'finished': {
          if (response.data.filename.startsWith('https:')) {
            onSuccess(response.data.filename);
          } else {
            onSuccess(undefined);
          }
          break;
        }
        case 'in-progress':
        default: {
          setTimeout(
            () => observeZipStatus(jsonFileS3Url, retriesLeft - 1, delay, onSuccess, onFail),
            delay
          );
          break;
        }
      }
    })
    .catch((error) => {
      logError(error);
      onFail();
    });
}

export function waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url: string): Promise<string | undefined> {
  const MAX_RETRIES = 15 * 60; // for 15 minutes
  const DELAY_BETWEEN_RETRIES = 1000; // 1 second

  return new Promise<string | undefined>((resolve, reject) => {
    observeZipStatus(jsonFileS3Url, MAX_RETRIES, DELAY_BETWEEN_RETRIES, resolve, reject);
  });
}

export class UserException {
  constructor(public error?: Error, public file?: File) {
    this.error = error;
    this.file = file;
  }
}
